import { useEffect } from "react";
import { useGraspStore } from "../../stores/graspStore";
import {
  ensureGraspOverlays,
  removeGraspOverlays,
  updateGraspOverlayPoses,
} from "../grasps/graspVisualizer";

export function useGraspOverlays({
  ikMarkerFocusGraspRef,
  sceneObjectsRef,
  sceneRef,
  selectedGraspByBodyRef,
  showGrasps,
}) {
  const graspsByBodyUuid = useGraspStore((s) => s.graspsByBodyUuid);

  useEffect(() => {
    const onSelectedGrasp = (event) => {
      const detail = event?.detail || {};
      const bodyUuid = typeof detail.targetBodyUuid === "string" ? detail.targetBodyUuid : "";
      const sourceIndex = Number(detail.sourceIndex);
      if (!bodyUuid || !Number.isFinite(sourceIndex)) return;
      selectedGraspByBodyRef.current[bodyUuid] = sourceIndex;
    };
    const onMarkerVisibility = (event) => {
      ikMarkerFocusGraspRef.current = Boolean(event?.detail?.visible);
    };
    const onIkClear = () => {
      selectedGraspByBodyRef.current = {};
    };
    window.addEventListener("ik-selected-grasp", onSelectedGrasp);
    window.addEventListener("ik-target-marker-visibility", onMarkerVisibility);
    window.addEventListener("ik-target-clear", onIkClear);
    return () => {
      window.removeEventListener("ik-selected-grasp", onSelectedGrasp);
      window.removeEventListener("ik-target-marker-visibility", onMarkerVisibility);
      window.removeEventListener("ik-target-clear", onIkClear);
    };
  }, [ikMarkerFocusGraspRef, selectedGraspByBodyRef]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    for (const [bodyUuid, grasps] of Object.entries(graspsByBodyUuid)) {
      const group = ensureGraspOverlays(scene, bodyUuid, grasps);
      group.visible = showGrasps;
      const poseGroup = sceneObjectsRef.current?.[bodyUuid];
      if (poseGroup) {
        updateGraspOverlayPoses(scene, bodyUuid, poseGroup, {
          selectedSourceIndex: selectedGraspByBodyRef.current?.[bodyUuid] ?? null,
          focusSelectedOnly: ikMarkerFocusGraspRef.current,
          highlightSelected: true,
        });
      }
    }

    scene.children
      .filter((obj) => obj.userData?.isGraspOverlay)
      .forEach((obj) => {
        if (!graspsByBodyUuid[obj.userData.bodyUuid]) {
          removeGraspOverlays(scene, obj.userData.bodyUuid);
        }
      });
  }, [
    graspsByBodyUuid,
    ikMarkerFocusGraspRef,
    sceneObjectsRef,
    sceneRef,
    selectedGraspByBodyRef,
    showGrasps,
  ]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.children
      .filter((obj) => obj.userData?.isGraspOverlay)
      .forEach((obj) => {
        obj.visible = showGrasps;
      });
  }, [sceneRef, showGrasps]);

  useEffect(() => {
    let animId;
    const tick = () => {
      animId = requestAnimationFrame(tick);
      const scene = sceneRef.current;
      if (!scene || !showGrasps) return;
      const objectsMap = sceneObjectsRef.current;
      if (!objectsMap) return;
      for (const bodyUuid of Object.keys(graspsByBodyUuid)) {
        const poseGroup = objectsMap[bodyUuid];
        if (poseGroup) {
          updateGraspOverlayPoses(scene, bodyUuid, poseGroup, {
            selectedSourceIndex: selectedGraspByBodyRef.current?.[bodyUuid] ?? null,
            focusSelectedOnly: ikMarkerFocusGraspRef.current,
            highlightSelected: true,
          });
        }
      }
    };
    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [
    graspsByBodyUuid,
    ikMarkerFocusGraspRef,
    sceneObjectsRef,
    sceneRef,
    selectedGraspByBodyRef,
    showGrasps,
  ]);
}
